import { getContentfulClient } from './client';
import { resolveAssetUrl, unwrapField, unwrapLinkField } from './utils';

const CONTENT_TYPE_MEMBERS = 'members';

/** Sections that use hardcoded Kalyan / Katie; CMS entries in these are ignored. */
const STATIC_ONLY_SECTIONS = new Set(['Principal Investigator', 'Group Admin']);

export const SECTIONS_ORDER = [
  'Admin',
  'Project Manager',
  'Visiting Scholars/Affiliates',
  'Members',
  'Alumni',
  'More Alumni',
  'Collaborators',
];

function isExcludedName(name) {
  const n = String(name || '').toLowerCase().trim();
  if (!n) {
    return true;
  }
  if (n === 'kalyan veeramachaneni' || n.includes('kalyan veeramachaneni')) {
    return true;
  }
  if (n === "katie o'reilly" || n === 'katie oreilly' || n.includes("katie o'reilly")) {
    return true;
  }
  return false;
}

/**
 * @param {Map<string, any>} entriesById
 * @param {import('contentful').Entry<any> | any | undefined} fieldValue
 */
function resolveEntryLink(fieldValue, entriesById) {
  if (!fieldValue) {
    return null;
  }
  const v = unwrapLinkField(fieldValue);
  if (v && typeof v === 'object' && v.fields) {
    return v;
  }
  if (v && v.sys?.type === 'Link' && v.sys.linkType === 'Entry' && v.sys.id) {
    return entriesById.get(v.sys.id) || null;
  }
  return null;
}

/**
 * @param {import('contentful').Entry<any> | null} degreeEntry
 */
function getDegreeLabel(degreeEntry) {
  if (!degreeEntry?.fields) {
    return '';
  }
  const d = degreeEntry.fields;
  const a =
    unwrapField(d.title) ||
    unwrapField(d.name) ||
    unwrapField(d.label) ||
    unwrapField(d.degree) ||
    unwrapField(d.shortName) ||
    '';
  return String(a).trim();
}

/**
 * @param {import('contentful').Entry<any>} entry
 * @param {Map<string, any>} entriesById
 * @param {Map<string, any>} assetsById
 */
function mapMemberEntry(entry, entriesById, assetsById) {
  const f = entry.fields || {};
  const name = String(unwrapField(f.name) || '').trim();
  const section = String(unwrapField(f.peoplePageSection) || '').trim() || null;
  const type = String(unwrapField(f.type) || '').trim();
  const status = String(unwrapField(f.status) || '').trim();
  const year = String(unwrapField(f.year) || '').trim();
  const linkedInUrl = String(unwrapField(f.linkedInUrl) || '').trim() || null;

  const pictureUrl = resolveAssetUrl(unwrapLinkField(f.picture), assetsById);
  const thesisPdfUrl = resolveAssetUrl(unwrapLinkField(f.thesisPdf), assetsById);

  const degreeEntry = resolveEntryLink(f.degree, entriesById);
  const degreeLabel = getDegreeLabel(degreeEntry);

  return {
    id: entry.sys.id,
    name,
    peoplePageSection: section,
    type,
    status,
    year,
    degreeLabel,
    pictureUrl: pictureUrl || null,
    linkedInUrl,
    thesisPdfUrl,
  };
}

/**
 * Name + Contentful `type` only (e.g. PhD, Masters) on cards with a photo.
 * @param {ReturnType<typeof mapMemberEntry>} m
 */
function toMemberStyleCardProps(m) {
  return {
    id: m.id,
    name: m.name,
    photo: m.pictureUrl || null,
    type: String(m.type || '').trim() || null,
  };
}

/**
 * @param {ReturnType<typeof mapMemberEntry>} m
 */
function toAlumniCardProps(m) {
  return {
    id: m.id,
    name: m.name,
    photo: m.pictureUrl || null,
    type: String(m.type || '').trim() || null,
  };
}

/**
 * @param {ReturnType<typeof mapMemberEntry>} m
 */
function toVisitingCardProps(m) {
  return {
    id: m.id,
    name: m.name,
    photo: m.pictureUrl || null,
    type: String(m.type || '').trim() || null,
  };
}

/**
 * Same pattern as static more-alumni lines, e.g. "Grace Song (Masters 2024)".
 * @param {ReturnType<typeof mapMemberEntry>} raw
 */
function formatMoreAlumniListLine(raw) {
  const name = String(raw.name || '').trim();
  const t = String(raw.type || '').trim();
  const y = String(raw.year || '').trim();
  const paren = [t, y].filter(Boolean).join(' ').trim();
  if (paren) {
    return `${name} (${paren})`;
  }
  return name;
}

/**
 * Fetches and groups all member entries. Ignores static-only sections and Kalyan/Katie rows.
 * @returns {Promise<Record<string, any[]> | null>} grouped by peoplePageSection, or null if unconfigured
 */
export async function fetchMembersGroupedBySection() {
  const client = getContentfulClient();
  if (!client) {
    return null;
  }

  const items = [];
  const entriesById = new Map();
  const assetsById = new Map();
  const limit = 100;
  let skip = 0;

  for (;;) {
    const res = await client.getEntries({
      content_type: CONTENT_TYPE_MEMBERS,
      limit,
      skip,
      /* name is Text in Contentful — cannot use order=fields.name; we sort in JS by name per section */
      include: 10,
    });
    (res.items || []).forEach((e) => {
      if (e?.sys?.id) {
        items.push(e);
        entriesById.set(e.sys.id, e);
      }
    });
    const incEnt = res.includes?.Entry || res.includes?.entry || [];
    incEnt.forEach((e) => {
      if (e?.sys?.id) {
        entriesById.set(e.sys.id, e);
      }
    });
    (res.includes?.Asset || res.includes?.asset || []).forEach((a) => {
      if (a?.sys?.id) {
        assetsById.set(a.sys.id, a);
      }
    });
    if ((res.items || []).length < limit) {
      break;
    }
    skip += limit;
  }

  const grouped = {
    Admin: [],
    'Project Manager': [],
    'Visiting Scholars/Affiliates': [],
    Members: [],
    Alumni: [],
    'More Alumni': [],
    Collaborators: [],
  };

  for (const entry of items) {
    const raw = mapMemberEntry(entry, entriesById, assetsById);
    if (!raw.name || isExcludedName(raw.name)) {
      continue;
    }
    const section = raw.peoplePageSection;
    if (!section || !Object.prototype.hasOwnProperty.call(grouped, section)) {
      /* unknown section: skip to avoid wrong placement */
      continue;
    }
    if (STATIC_ONLY_SECTIONS.has(section)) {
      continue;
    }

    if (section === 'Alumni') {
      grouped[section].push(toAlumniCardProps(raw));
    } else if (section === 'Visiting Scholars/Affiliates') {
      grouped[section].push(toVisitingCardProps(raw));
    } else if (section === 'More Alumni') {
      grouped[section].push({ id: raw.id, name: formatMoreAlumniListLine(raw) });
    } else if (section === 'Collaborators') {
      grouped[section].push({ id: raw.id, name: raw.name });
    } else {
      grouped[section].push(toMemberStyleCardProps(raw));
    }
  }

  Object.keys(grouped).forEach((k) => {
    grouped[k].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  });

  return grouped;
}

export { isExcludedName };
